import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import type { IAnalyticsProvider, LegalDoc } from '@puzzlemania/core';

/**
 * Canonical hosted origin for the static legal pages (WP-NAV.2). Native builds
 * must use an absolute URL — the app's own origin there is capacitor://localhost,
 * whose /legal/*.html the system browser can't reach. Web opens same-origin so
 * preview/staging deployments read their own copy.
 *
 * Placeholder-domain caveat: same as sitemap.xml — update if the *.pages.dev
 * subdomain changes or a custom domain lands (WP-WEB.9).
 */
const CANONICAL_ORIGIN = 'https://puzzlemania.pages.dev';

function legalDocUrl(doc: LegalDoc): string {
  const path = `/legal/${doc}.html`;
  return Capacitor.isNativePlatform() ? `${CANONICAL_ORIGIN}${path}` : path;
}

/**
 * Module 28 (WP-NAV.3) — the single app-wide way to show a legal document:
 * externally (new tab on web, system browser on native), never via an SPA
 * route change, so the active screen — especially a running game — is left
 * completely untouched.
 *
 * `from` feeds the `platform_legal_opened` analytics event: 'lobby' for the
 * AppLayout settings popup, or the game id when called through IGameContext
 * (GameContextBuilder supplies it automatically).
 */
export function makeOpenLegalDoc(
  analytics: IAnalyticsProvider,
): (doc: LegalDoc, from: string) => void {
  return (doc, from) => {
    analytics.logEvent('platform_legal_opened', { doc, from });
    const url = legalDocUrl(doc);
    if (Capacitor.isNativePlatform()) {
      void Browser.open({ url });
    } else {
      window.open(url, '_blank', 'noopener');
    }
  };
}
